import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { fetchInventoryRegistries, getTotalsUsedByProduct, exportInventoryRegistries } from '../services/inventoryRegistries.services';
import { inventoryRegistriesColumns, columnsExcel } from '../utils/inventoryRegistriesColumns';
import { fetchProducts } from '@/pages/parametrizacion/products/Services/products.services';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import { deleteInventoryRegistry } from '../services/inventoryRegistries.services';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const ITEMS_PER_PAGE = 10;

const ConsumosTable = ({ idLavanderia, refreshKey, isExternalLoading = false }) => {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalsUsed, setTotalsUsed] = useState([]);
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({}); // expects { fecha: {$gte,$lte}, 'producto.id': '...' }
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showTotalsModal, setShowTotalsModal] = useState(false);
  const [exportingTotals, setExportingTotals] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { idLavanderia, tipoMovimiento: 'consumo', ...filters };
      const { data: rows, totalRecords } = await fetchInventoryRegistries({ limit: ITEMS_PER_PAGE, offset: currentPage, query });
      setData(rows);
      setTotalItems(totalRecords);
    } catch (e) {
      console.error('Error cargando consumos:', e);
      toast({ title: 'Error', description: 'No se pudieron cargar los consumos.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [idLavanderia, currentPage, filters, toast]);

  useEffect(() => { load(); }, [load, refreshKey, filters, currentPage]);

  // Totales de cantidad usada por producto
  useEffect(() => {
    const fetchTotals = async () => {
      if (!idLavanderia) {
        setTotalsUsed([]);
        return;
      }
      setLoadingTotals(true);
      try {
        const from = filters?.fecha?.$gte ? filters.fecha.$gte : undefined;
        const to = filters?.fecha?.$lte ? filters.fecha.$lte : undefined;
        const data = await getTotalsUsedByProduct({ lavanderiaId: idLavanderia, from, to });
        let arr = Array.isArray(data) ? data : [];
        if (filters['producto.id']) {
          arr = arr.filter(t => (t?.producto?.id) === filters['producto.id']);
        }
        // Ordenar de mayor a menor consumo
        arr.sort((a, b) => (Number(b?.totalUsado) || 0) - (Number(a?.totalUsado) || 0));
        setTotalsUsed(arr);
      } catch (e) {
        setTotalsUsed([]);
      } finally {
        setLoadingTotals(false);
      }
    };
    fetchTotals();
  }, [idLavanderia, refreshKey, filters]);

  // Load products for filter options
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await fetchProducts({ limit: 500, offset: 1, query: {} });
        setProducts(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Columnas relevantes para consumos
  const columns = inventoryRegistriesColumns.filter(c => [
    'fecha',
    'producto',
    'tipoMovimiento',
    'cantidadInicial',
    'cantidadUsada',
    'cantidadRestante',
    'costoUnitario',
    'costoTotal',
    'observacion',
    'createdAt'
  ].includes(c.id));

  const actionsColumn = {
    id: 'opciones',
    key: 'opciones',
    label: 'Opciones',
    actions: [
      {
        key: 'delete',
        icon: 'Trash',
        tooltip: 'Eliminar',
        showWhen: () => isAdmin(),
        className: 'text-red-500',
      },
    ],
  };
  const columnsWithActions = [actionsColumn, ...columns];

  const handleAction = async (actionKey, row) => {
    if (actionKey === 'delete') {
      if (!isAdmin()) return;
      const ok = window.confirm('¿Deseas eliminar este registro? Esta acción no se puede deshacer.');
      if (!ok) return;
      try {
        await deleteInventoryRegistry(row._id || row.id);
        toast({ title: 'Eliminado', description: 'Registro eliminado correctamente.' });
        await load();
      } catch (e) {
        toast({ title: 'Error', description: 'No se pudo eliminar el registro.', variant: 'destructive' });
      }
    }
  };

  const productOptions = products.map(p => ({ value: p._id || p.id, label: p.nombre }));

  const filterFields = [
    { id: 'fecha', label: 'Rango de fechas', type: 'daterange' },
    { id: 'producto.id', label: 'Producto', type: 'select', options: [{ value: '', label: '-- Todos --' }, ...productOptions] },
  ];

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const handleRemoveFilter = (key) => {
    const nf = { ...filters };
    delete nf[key];
    setFilters(nf);
  };

  const handleExportTotals = async () => {
    setExportingTotals(true);
    try {
      const dataToExport = totalsUsed.map(t => ({
        Producto: t?.producto?.nombre || 'N/A',
        'Total Consumido': Number(t?.totalUsado || 0),
        Unidad: t?.producto?.unidadMedida || 'N/A',
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Totales');
      XLSX.writeFile(workbook, 'totales-consumos.xlsx');

      toast({ title: 'Éxito', description: 'Totales exportados correctamente.' });
    } catch (error) {
      console.error('Error al exportar totales:', error);
      toast({ title: 'Error', description: 'No se pudieron exportar los totales.', variant: 'destructive' });
    } finally {
      setExportingTotals(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">Tabla de Consumos</CardTitle>
            <CardDescription>
              Movimientos tipo consumo
              <div className="mt-1 text-[11px] text-muted-foreground">
                <span className="font-medium">TOTALES:</span>{' '}
                {loadingTotals ? 'Cargando…' : (
                  totalsUsed.length === 0 ? '—' : (
                    <>
                      {totalsUsed.slice(0, 5)
                        .map(t => `${t?.producto?.nombre || 'Producto'}: ${(Number(t?.totalUsado)||0).toLocaleString('es-CO')}`)
                        .join(' · ')}
                      {totalsUsed.length > 5 && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 ml-2 text-[11px] text-blue-600 hover:text-blue-800"
                          onClick={() => setShowTotalsModal(true)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver todos ({totalsUsed.length})
                        </Button>
                      )}
                    </>
                  )
                )}
              </div>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportExcelButton
              fileName="Consumos"
              columns={columnsExcel}
              filters={{ "lavanderia.id": idLavanderia, tipoMovimiento: 'consumo', ...filters }}
              service={exportInventoryRegistries}
            />
            <Button variant="outline" size="sm" onClick={() => setIsDrawerOpen(true)} disabled={loadingProducts}>
              {loadingProducts ? 'Cargando…' : 'Filtros'}
            </Button>
          </div>
        </div>
        <div className="mt-2">
          <AppliedFilters filters={filters} fields={filterFields} onRemoveFilter={handleRemoveFilter} />
        </div>
      </CardHeader>
      <CardContent>
        <DataTable data={data} columns={columnsWithActions} isLoading={loading || isExternalLoading} showCreateButton={false} onAction={handleAction} />
        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </CardContent>
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onChange={handleFilterApply}
        onApply={handleFilterApply}
        initialDay={false}
      />

      {/* Modal de Totales */}
      <Dialog open={showTotalsModal} onOpenChange={setShowTotalsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Totales de Consumos por Producto</DialogTitle>
            <DialogDescription>
              Resumen completo de cantidades consumidas por producto
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {totalsUsed.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay datos disponibles</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 pb-2 border-b font-semibold text-sm">
                  <div>Producto</div>
                  <div className="text-right">Total Consumido</div>
                  <div className="text-right">Unidad</div>
                </div>
                {totalsUsed.map((t, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2 py-2 border-b text-sm hover:bg-muted/50">
                    <div className="font-medium">{t?.producto?.nombre || 'N/A'}</div>
                    <div className="text-right">{(Number(t?.totalUsado) || 0).toLocaleString('es-CO')}</div>
                    <div className="text-right text-muted-foreground">{t?.producto?.unidadMedida || 'Litros'}</div>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowTotalsModal(false)}
            >
              Cerrar
            </Button>
            <Button
              onClick={handleExportTotals}
              disabled={exportingTotals || totalsUsed.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {exportingTotals ? (
                <>
                  <Download className="mr-2 h-4 w-4 animate-pulse" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exportar a Excel
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ConsumosTable;
